import { SavedListOrderByRule, SAVED_LISTS_PAGE_SIZE } from '../../../Profile/SavedListsTabContent/constants';

// orderBy values the players-feed endpoint (TMS-2174) accepts. Story 17 sorts on identity + the two
// rating columns; Story 18 (TMS-2109) adds the two activity sorts backing the Recent Results / Event
// Activity columns.
export const PlayerSortColumn = {
    NAME: 'Name',
    SINGLES: 'RatingSingles',
    DOUBLES: 'RatingDoubles',
    ACTIVITY_RESULTS: 'ActivityByResults',
    ACTIVITY_EVENTS: 'ActivityByEvents',
};

export { SavedListOrderByRule, SAVED_LISTS_PAGE_SIZE };

export const COLUMN_LABELS = {
    NAME: 'Name',
    RECENT_RESULTS: 'Recent Results',
    EVENT_ACTIVITY: 'Event Activity',
};

// The singles/doubles rating columns read as "UTR" + the shared singles (1-person) / doubles (2-person)
// icon, matching the header design (185-11826) and the app's other player tables. The same icons label
// the two ratings on the mobile card (128-8122), which has no column headers.
export const UTR_COLUMN_LABEL = 'UTR';
export const SINGLES_ICON = '/img/v3/icons/icon-singles-black.svg';
export const DOUBLES_ICON = '/img/v3/icons/icon-doubles-black.svg';

// Up/down arrows icon for the mobile sort trigger (Figma 101-7453, 16×16).
export const SORT_ICON = '/img/v3/icons/icon-sort-arrows.svg';

export const META_SEPARATOR = ' • ';

export const EMPTY_MESSAGE = 'No players in this list yet.';

// Placeholder shown in a Recent Results / Event Activity cell (or mobile section) when the player has
// no activity of that kind.
export const ACTIVITY_EMPTY = '—';

// Recent Results (TMS-2183 feed `item.recentResults[]`). matchType picks which opponent rating shows
// (singlesRating on Singles, doublesRating on Doubles) and which person icon renders.
export const MatchType = {
    SINGLES: 'Singles',
    DOUBLES: 'Doubles',
};

// Event Activity (TMS-2183 feed `item.eventActivity[]`). `source` composes the event link (Tms → the
// TMS event profile, Leagues → the Leagues event page); `status` drives the label and ordering.
export const EventActivitySource = {
    TMS: 'Tms',
    LEAGUES: 'Leagues',
};

export const EventActivityStatus = {
    IN_PROGRESS: 'InProgress',
    UPCOMING: 'Upcoming',
    COMPLETED: 'Completed',
};

export const EVENT_STATUS_LABELS = {
    [EventActivityStatus.IN_PROGRESS]: 'In Progress',
    [EventActivityStatus.UPCOMING]: 'Upcoming',
    [EventActivityStatus.COMPLETED]: 'Completed',
};

// At most two events per cell, most-relevant first: in-progress, then upcoming, then completed.
export const EVENT_STATUS_PRIORITY = {
    [EventActivityStatus.IN_PROGRESS]: 0,
    [EventActivityStatus.UPCOMING]: 1,
    [EventActivityStatus.COMPLETED]: 2,
};

export const MAX_ACTIVITY_ROWS = 2;

// A given date renders `Jun 11, '26` in a result row; an upcoming event shows only `Jul 27`.
export const RESULT_DATE_FORMAT = 'MMM D, \'YY';
export const EVENT_DATE_FORMAT = 'MMM D';

// Opponent profile link — composed from the feed's `playerId` (the feed sends no profileUrl). These
// new-tab attributes are shared by every row link that opens in a new tab so the member keeps their
// place on the Saved Lists tab: the opponent link, the player name link, and the event link (185-11717).
export const OPPONENT_PROFILE_PATH = '/profiles';
export const OPPONENT_LINK_TARGET = '_blank';
export const OPPONENT_LINK_REL = 'noopener noreferrer';

// Event links stay same-tab, matching the app's existing event links. eventActivityUrl maps the feed's
// Leagues source to ImportedEventSources.LEAGUE and everything else to this TMS source key, then routes
// through the shared getEventRedirectUrl composer.
export const TMS_EVENT_SOURCE = 'tms-event';

export const SEE_MORE_LABEL = 'See More';
export const SEE_LESS_LABEL = 'See Less';

// Mobile players-header chrome (101-7453): the recent-activity legend and the Expand All / Collapse All
// control that drives every card's expansion at once.
export const LEGEND_LABEL = 'Recent Activity';
export const EXPAND_ALL_LABEL = 'Expand All';
export const COLLAPSE_ALL_LABEL = 'Collapse All';

// Row kebab item that opens the Add/Edit Player Note modal. Its `testId` pins the ticket's
// `list-detail.player-row-{id}.menu.note` contract, which drops the `item-` prefix the sibling
// remove item (`.menu.item-remove`) carries — PopOverMenu honours the per-item override.
export const NOTE_MENU_VALUE = 'note';
export const NOTE_MENU_LABEL = 'Add/Edit Player Note';
export const NOTE_MENU_TESTID = 'note';
export const REMOVE_MENU_VALUE = 'remove';
export const REMOVE_MENU_LABEL = 'Remove Player from List';
// Remove pins its testid so the mobile Actions sheet and the desktop PopOverMenu (whose default suffix
// is already `item-remove`) share one contract.
export const REMOVE_MENU_TESTID = 'item-remove';

// Mobile row-actions sheet (371-12193): an iOS-style centered action sheet with its own copy — the
// two actions read as sentences here, distinct from the desktop kebab's shorter labels, plus a Cancel.
export const ACTIONS_SHEET_TITLE = 'Actions';
export const ACTIONS_NOTE_LABEL = 'Save a note about this player';
export const ACTIONS_REMOVE_LABEL = 'Remove player from list';
export const ACTIONS_CANCEL_LABEL = 'Cancel';

// Add/Edit Player Note modal (185-11716). Mirrors SavedPlayerListConstant.MaxNoteLength (1000) on the
// server; the textarea caps input so the API 400 SavedPlayerNoteTooLong can't normally be reached.
export const MAX_NOTE_LENGTH = 1000;
export const NOTE_MODAL_TITLE = 'Add/Edit Player Note';
export const NOTE_PLACEHOLDER = 'Enter your notes here';
export const CLEAR_NOTE_LABEL = 'Clear Note';

// The default server order is recent-activity-first (no orderBy). Recent Activity isn't a selectable
// Story-17 option (that's Story 18), so it only ever appears as the read-only default label.
export const DEFAULT_SORT_LABEL = 'Recent Activity';

export const SORT_SHEET_TITLE = 'Sort Player List';
export const SORT_SHEET_HEADING = 'Sort Players by:';
export const SORT_SHEET_APPLY_LABEL = 'Apply';

// The mobile "Sort Player List" sheet (101-8030): five radio options, each a bold title over a grey
// subtitle. `label` is the concise summary shown on the sort trigger; `title`/`subtitle` render the
// radio row; `testId` pins the ticket's `sort-player-list.{testId}` contract for the two activity
// options and stays consistent for the rest. Selecting one and tapping Apply drives the feed orderBy.
export const MOBILE_SORT_OPTIONS = [
    { value: 'name', label: 'Name', title: 'Name', subtitle: 'Alphabetical', testId: 'name', orderBy: PlayerSortColumn.NAME, orderByRules: SavedListOrderByRule.ASCENDING },
    { value: 'rating-high', label: 'Rating High to Low', title: 'Rating', subtitle: 'High to Low', testId: 'rating-high', orderBy: PlayerSortColumn.SINGLES, orderByRules: SavedListOrderByRule.DESCENDING },
    { value: 'rating-low', label: 'Rating Low to High', title: 'Rating', subtitle: 'Low to High', testId: 'rating-low', orderBy: PlayerSortColumn.SINGLES, orderByRules: SavedListOrderByRule.ASCENDING },
    { value: 'activity-results', label: 'Activity (Results)', title: 'Activity (Results)', subtitle: 'Players who have posted recent results.', testId: 'activity-results', orderBy: PlayerSortColumn.ACTIVITY_RESULTS, orderByRules: SavedListOrderByRule.DESCENDING },
    { value: 'activity-events', label: 'Activity (Events)', title: 'Activity (Events)', subtitle: 'Players who have recently registered for events.', testId: 'activity-events', orderBy: PlayerSortColumn.ACTIVITY_EVENTS, orderByRules: SavedListOrderByRule.DESCENDING },
];
